// @ts-nocheck

import { filter, keyBy } from 'lodash';

import { type OutcomeState } from '../states/entities/outcomes';
import { BetCategories } from '../types/bet';
import { PresentationModes, type PresentationMode } from '../types/betGroup';
import { MarketIds } from '../types/market';
import { getBetCategoryIdFromFilterId, getPlayerIdFromFilterId } from '../types/matchBetFilters';
import { OutcomeModes } from '../types/outcome';

import OddUtils from './odd';

type Outcomes = Array<OutcomeState>;

export const getOrderedOutcomes = (outcomes: Outcomes, template: string): Outcomes => {
  const allOutcomeCodes = outcomes.map((x) => x.code);
  const predefinedOrders = getPredefinedOrdersByTemplate(template);
  const outcomeByCodeMap: { [string]: OutcomeState } = keyBy(outcomes, 'code');

  const predefinedOrder = (predefinedOrders || []).reduce((correspondingOrder, curr) => {
    if (!correspondingOrder) {
      if (allOutcomeCodes.filter((code) => curr.indexOf(code) !== -1).length === allOutcomeCodes.length) {
        return curr;
      }
    }
    return correspondingOrder;
  }, null);

  if (predefinedOrder) {
    return predefinedOrder.reduce((acc, curr) => {
      if (curr && outcomeByCodeMap[curr]) {
        acc.push(outcomeByCodeMap[curr]);
      }
      return acc;
    }, []);
  }
  return outcomes;
};

const getPredefinedOrdersByTemplate = (template: string): Array<Array<string | null>> | null => {
  return betOddOrderByTemplate[template] || null;
};

export const betOddOrderByTemplate: { [string]: Array<Array<string | null>> } = {
  yesno: [
    ['yes', null, 'no'],
    ['competiteur 1', null, 'competiteur 2'],
  ],
  '2way': [
    ['1', null, '2'],
    ['red', null, 'green'],
  ],
  '3way': [
    ['1', 'none', '2'],
    ['1', 'x', '2'],
    ['home', 'none', 'away'],
    ['0-8', '9-11', '12+'],
    ['<89.5', '90-100', '>100.5'],
  ],
  '3choices': [['0', '1', '2+']],
  '12none': [['1', 'none', '2']],
  '2half': [
    ['1st', 'equal', '2nd'],
    ['first half', 'equals', 'second half'],
  ],
  doublechance: [['1x', '12', 'x2']],
  goalnogoal: [['goal', null, 'nogoal']],
  hna: [['home', 'none', 'away']],
  overunder: [
    ['over', null, 'under'],
    ['o', null, 'u'],
    ['2', null, '1'],
    ['over 30.5', null, 'under 30.5'],
    ['41+', null, '0-40'],
  ],
  numberset2: [
    ['2', null, '3'],
    ['2 sets', null, '3 sets'],
  ],
  numberset3: [
    ['3', '4', '5'],
    ['3 sets', '4 sets', '5 sets'],
  ],
  numberset4: [
    ['4', '5', '6', '7'],
    ['4 sets', '5 sets', '6 sets', '7 sets'],
    ['0 sets', '1 sets', '2 sets', '3 sets'],
  ],
  winningmargingbut: [
    ['ht > 2', 'ht 2', 'ht 1', 'x', 'at 1', 'at 2', 'at > 2'],
    ['ht > 14', 'ht 8-14', 'ht 1-7', 'x', 'at 1-7', 'at 8-14', 'at > 14'],
    ['>14', '14 till 8', '7 till 1', '0', '-1 till -7', '-8 till -14', '<-14'],
    ['>13', '7 till 13', '1 till 6', '0', '-6 till -1', '-13 till -7', '<-13'],
    ['ht > 12', 'ht 7-12', 'ht 1-6', 'x', 'at 1-6', 'at 7-12', 'at > 12'],
    ['ht > 10', 'ht 6-10', 'ht 1-5', 'x', 'at 1-5', 'at 6-10', 'at > 10'],
    ['ht > 10', 'ht 6-10', 'ht 1-5', 'draw', 'at 1-5', 'at 6-10', 'at > 10'],
    ['ht > 4', 'ht 3-4', 'ht 1-2', 'draw', 'at 1-2', 'at 3-4', 'at > 4'],
    ['>=+3', '+2', '+1', '0', '-1', '-2', '<=-3'],
  ],
  '2-2': [
    ['0-1 goals', '2-3 goals', '4-5 goals', '6+'],
    ['0-1', '2-3', '4-5', '6+'],
    ['1st period', '2nd period', '3rd period', 'equal'],
    ['4', '5', '6', '>6'],
    ['touchdown', 'fieldgoal', 'safety', 'none'],
    ['0', '1', '2', '3+'],
  ],
  '3-3-1': [
    ['0', '1', '2', '3', '4', '5', '6+'],
    ['1-15', '16-30', '31-45', '46-60', '61-75', '76-90', 'none'],
    ['min 01-15', 'min 16-30', 'min 31-45', 'min 46-60', 'min 61-75', 'min 76-90', 'no goal'],
    ['<=4', '5', '6', '7', '8', '9', '10+'],
  ],
  '3-2': [
    ['0', '1', '2', '3', '4+'],
    ['0-49', '50-99', '100-119', '120-146', '147+'],
  ],
  oddeven: [['odd', null, 'even']],
  '3-3-3-1': [
    ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9+'],
    ['<4', '4', '5', '6', '7', '8', '9', '10', '11', '12+'],
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'equal'],
    ['0-3', '4', '5', '6', '7', '8', '9', '10', '11', '12+'],
  ],
  numberset6: [
    ['0 sets', '1 sets', '2 sets', '3 sets', '4 sets', '5 sets'],
    ['0', '1', '2', '3', '4', '5'],
  ],
  totalmarging: [
    ['<7', '7-13', '14-20', '21-27', '28-34', '35-41', '>41'],
    ['0 to 6', '7 to 13', '14 to 20', '21 to 27', '28 to 34', '35 to 41', '42 plus'],
    ['<20', '20-22', '23-25', '26-28', '29-31', '32-34', '>34'],
  ],
  '3-3': [
    ['0', '1', '2', '3', '4', '5+'],
    ['0', '1', '2', '3', '4', '5'],
    ['yellow', 'green', 'brown', 'blue', 'pink', 'black'],
  ],
  '3-3-2': [['0', '1', '2', '3', '4', '5', '6', '7']],
  '3-3-3': [['<=1', '2', '3', '4', '5', '6', '7', '8', '9+']],
};

export const longBetSettingByTemplate: { [string]: { [string]: number } } = {
  list: { lineThreshold: 4, previewLines: 3 },
  listalpha: { lineThreshold: 4, previewLines: 3 },
  listodd: { lineThreshold: 4, previewLines: 3 },
  numberlist: { lineThreshold: 4, previewLines: 3 },
  overunder: { lineThreshold: 4, previewLines: 3, outcomesPerLine: 2 },
  winningmargingbut: { lineThreshold: 4, previewLines: 3 },
};

const filterOutcomesByTemplate = (
  betTemplate: string,
  outcomes: Outcomes,
  matchIsAvailable: boolean,
  betIsAvailable: boolean,
  odds: { [number]: number },
): Outcomes => {
  if (
    betTemplate === 'listodd' ||
    betTemplate === 'listalpha' ||
    betTemplate === 'score' ||
    betTemplate === 'setscore'
  ) {
    return filter(
      outcomes,
      (x) =>
        OddUtils.getMode({
          matchIsAvailable,
          betIsAvailable,
          betTemplate,
          oddValue: odds[x],
          outcomeIsAvailable: x.available ?? true,
        }) !== OutcomeModes.STOP,
    );
  }
  return outcomes;
};

export const specifiersToHash = (specifiers: string | undefined): Record<string, string> => {
  const result = {};
  if (specifiers) {
    specifiers.split('|').forEach((oneSpecifier) => {
      const [key, value] = oneSpecifier.split('=');
      result[key] = value;
    });
  }
  return result;
};

export const getSrPlayerIdsFromSBV = (sbv?: string): Array<string> | null => {
  if (typeof sbv === 'string') {
    const patterns = [
      /^player=sr:player:([\d]+)$/,
      /^player=sr:player:([\d]+)\|.+$/,
      /^players=sr:([\d]+)-sr:([\d]+)\|.+$/,
      /^players=sr:([\d]+)-sr:([\d]+)-sr:([\d]+)\|.+/,
      /^player1=sr:player:([\d]+)\|player2=sr:player:([\d]+)$/,
      /^variant=pre:playerprops:[\d]+:([\d]+)$/,
      /^pre:playerprops:[\d]+:([\d]+):[\d]+$/,
      /^players:sr:([\d]+)\|sr:([\d]+)$/,
      /^players:sr:([\d]+)\|sr:([\d]+)\|sr:([\d]+)$/,
    ];

    if (patterns.some((pattern) => pattern.test(sbv))) {
      return [
        ...new Set(
          patterns.reduce(
            (acc, pattern) => [
              ...acc,
              ...(sbv
                .match(pattern)
                ?.slice(1)
                ?.map((k) => `sr:player:${k}`) ?? []),
            ],
            [],
          ),
        ),
      ];
    }
  }

  return null;
};

export const isBetPresentationModeSlider = (betPresentationMode: PresentationMode) => {
  return [PresentationModes.SLIDER_BY_BET, PresentationModes.SLIDER_BY_OUTCOME].includes(betPresentationMode);
};

export const getIsBetValidFromFilterPlayerKind = (
  bet: unknown,
  filterId: string,
  players: unknown,
  outcomes: unknown,
): boolean => {
  const playerIdFromFilter = getPlayerIdFromFilterId(filterId);

  if (playerIdFromFilter) {
    const srPlayerId = players[playerIdFromFilter]?.srPlayerId;
    const srPlayerIdsFromSBV = getSrPlayerIdsFromSBV(bet?.specialBetValue);

    if (Array.isArray(srPlayerIdsFromSBV)) {
      return !!~srPlayerIdsFromSBV.indexOf(srPlayerId);
    } else {
      const { outcomes: outcomeIds } = bet ?? {};

      if (outcomeIds) {
        return outcomeIds.some((outcomeId) => {
          const outcome = outcomes[outcomeId];

          if (outcome) {
            const outcomeSrPlayerId = outcome.srPlayerId;

            if (outcomeSrPlayerId) {
              return outcomeSrPlayerId === srPlayerId;
            }

            const { code } = outcome;

            if (code) {
              const srPlayerIdsFromCode = getSrPlayerIdsFromSBV(code);

              if (Array.isArray(srPlayerIdsFromCode)) {
                return !!~srPlayerIdsFromCode.indexOf(srPlayerId);
              }
            }
          }

          return false;
        });
      }
    }
  }

  return true;
};

export const getIsBetValidFromFilterBetCategoryKind = (bet: unknown, filterId: string): boolean => {
  const betCategoryIdFromFilterId = getBetCategoryIdFromFilterId(filterId);

  // Cas particulier pour ne pas afficher la matrice "Joueur décisif" dans le filtre buteurs
  if (betCategoryIdFromFilterId === BetCategories.SCORERS_SOCCER) {
    if (bet?.marketId === MarketIds.MARKET_ID_SOCCER_DECISIVE_PLAYER) {
      return false;
    }
  }

  return (
    !betCategoryIdFromFilterId ||
    !bet.betCategories?.length ||
    !!bet.betCategories?.find((betCategory) => betCategoryIdFromFilterId === betCategory)
  );
};

export default {
  betOddOrderByTemplate,
  longBetSettingByTemplate,
  filterOutcomesByTemplate,
  specifiersToHash,
  getSrPlayerIdsFromSBV,
  isBetPresentationModeSlider,
};
